
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import Header from './components/Header';
import StatsCard from './components/StatsCard';
import { useAuth } from '../../hooks/useAuth';

// Helper function to get profile photos
function getProfilePhoto(name: string): string | null {
  const photoMap: { [key: string]: string } = {
    'Christine Bautista': 'https://public.readdy.ai/ai/img_res/66613fd7a3a5f1d69c19eaf38ad19818.jpg',
    'Jeremy Bautista': 'https://readdy.ai/api/search-image?query=Professional%20Filipino%20man%20with%20short%20dark%20hair%2C%20wearing%20business%20shirt%2C%20confident%20smile%2C%20office%20headshot%20style%2C%20clean%20white%20background%2C%20high%20quality%20portrait%20photography&width=150&height=150&seq=jeremy-bautista&orientation=squarish',
  };
  
  return photoMap[name] || null;
}

// Individual Underwriting Assistant Performance Component
function IndividualPerformance({ data }: { data: any[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Individual Underwriting Assistant Performance</h2>
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Filter out "Total" row and sort alphabetically by underwriting assistant name
  const filteredData = data.filter(item => 
    item.underwriting_assistant && 
    item.underwriting_assistant.toLowerCase() !== 'total'
  ).sort((a, b) => {
    const nameA = (a.underwriting_assistant || '').toLowerCase();
    const nameB = (b.underwriting_assistant || '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  return (
    <div className="space-y-4">
      <div className="mb-4">
        <h2 className="text-xl font-bold text-gray-900">Individual Underwriting Assistant Performance</h2>
        <p className="text-sm text-gray-600 mt-1">Detailed performance metrics for each underwriting assistant</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredData.map((employee, index) => {
          const employeeName = employee.underwriting_assistant || 'Unknown';
          const employeePhoto = getProfilePhoto(employeeName);
          
          const policies = parseInt(String(employee.policies || '0').replace(/[^0-9]/g, '')) || 0;
          const roadsides = parseInt(String(employee.roadsides || '0').replace(/[^0-9]/g, '')) || 0;
          
          // Parse broker fee safely
          let brokerFee = 0;
          let bfAverage = 0;
          if (employee.broker_fee) {
            const brokerFeeStr = String(employee.broker_fee).replace(/[$,]/g, '');
            brokerFee = parseFloat(brokerFeeStr) || 0;
          }
          if (policies > 0) {
            bfAverage = brokerFee / policies;
          }

          return (
            <div 
              key={`${employeeName}-${index}`}
              className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              {/* Employee Header */}
              <div className="flex items-center mb-4">
                {employeePhoto ? (
                  <img
                    src={employeePhoto}
                    alt={employeeName}
                    className="h-16 w-16 rounded-full object-cover mr-3"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(employeeName)}&background=3b82f6&color=ffffff&size=128`;
                    }}
                  />
                ) : (
                  <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <i className="ri-user-line text-2xl text-blue-600"></i>
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">{employeeName}</h3>
                  <p className="text-sm text-gray-500">Underwriting Assistant</p>
                </div>
              </div>

              {/* Metrics */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">📋 Policies</span>
                  <span className="font-semibold text-gray-900">{policies}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    📈 BF Average {bfAverage < 130 && bfAverage > 0 ? '💩' : ''}
                  </span>
                  <span className="font-semibold text-gray-900">
                    ${Math.round(bfAverage).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">💰 BF Total</span>
                  <span className="font-semibold text-gray-900">
                    ${Math.round(brokerFee).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">🚗 Roadsides</span>
                  <span className="font-semibold text-gray-900">{roadsides}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Performance Rankings Component
function PerformanceRankings({ data }: { data: any[] }) {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg">
        <h2 className="text-xl font-bold text-gray-900 mb-4">PH Performance Ranking</h2>
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Filter out "Total" row and sort by policies
  const filteredData = data.filter(item => 
    item.underwriting_assistant && 
    item.underwriting_assistant.toLowerCase() !== 'total'
  );

  const sortedData = [...filteredData].sort((a, b) => {
    const aPolicies = parseInt(String(a.policies || '0').replace(/[^0-9]/g, '')) || 0;
    const bPolicies = parseInt(String(b.policies || '0').replace(/[^0-9]/g, '')) || 0;
    return bPolicies - aPolicies;
  });

  const maxPolicies = Math.max(...sortedData.map(emp => {
    return parseInt(String(emp.policies || '0').replace(/[^0-9]/g, '')) || 0;
  }), 1);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-white border-b border-gray-200">
        <h3 className="text-base font-bold text-gray-900">PH Performance Ranking</h3>
        <p className="text-xs text-gray-600 mt-1">Ranked by Total Policies</p>
        <p className="text-xs text-blue-600 font-medium mt-1">
          Showing {sortedData.length} PH employees
        </p>
      </div>
      
      {/* List Items */}
      <div className="divide-y divide-gray-200">
        {sortedData.map((employee, index) => {
          const rank = index + 1;
          const employeeName = employee.underwriting_assistant || 'Unknown';
          const employeePhoto = getProfilePhoto(employeeName);
          
          const policies = parseInt(String(employee.policies || '0').replace(/[^0-9]/g, '')) || 0;
          const roadsides = parseInt(String(employee.roadsides || '0').replace(/[^0-9]/g, '')) || 0;
          
          // Parse broker fee safely
          let brokerFee = 0;
          if (employee.broker_fee) {
            const brokerFeeStr = String(employee.broker_fee).replace(/[$,]/g, '');
            brokerFee = parseFloat(brokerFeeStr) || 0;
          }

          // Get trophy emoji for top 3
          const getTrophyEmoji = (rank: number) => {
            switch (rank) {
              case 1: return '🏆';
              case 2: return '🥈';
              case 3: return '🥉';
              default: return null;
            }
          };

          const isTopThree = rank <= 3;
          const rankIcon = getTrophyEmoji(rank);

          return (
            <div 
              key={`${employeeName}-${index}`} 
              className={`px-4 py-3 ${isTopThree ? 'bg-yellow-50' : 'bg-white'}`}
            >
              {/* Agent Info Row */}
              <div className="flex items-center mb-2">
                {/* Rank Badge */}
                <div className="flex-shrink-0 mr-2">
                  {rankIcon ? (
                    <span className="text-xl">{rankIcon}</span>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-600">{rank}</span>
                    </div>
                  )}
                </div>

                {/* Avatar */}
                <div className="flex-shrink-0 mr-3">
                  {employeePhoto ? (
                    <img
                      src={employeePhoto}
                      alt={employeeName}
                      className="h-10 w-10 rounded-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(employeeName)}&background=3b82f6&color=ffffff&size=128`;
                      }}
                    />
                  ) : (
                    <img
                      className="h-10 w-10 rounded-full object-cover"
                      src={`https://ui-avatars.com/api/?name=${encodeURIComponent(employeeName)}&background=3b82f6&color=ffffff&size=128`}
                      alt={employeeName}
                    />
                  )}
                </div>

                {/* Name */}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-gray-900">
                    {employeeName}
                  </div>
                </div>
              </div>

              {/* Metrics Row */}
              <div className="flex items-center justify-end space-x-4 text-xs">
                {/* Policies */}
                <div className="flex items-center">
                  <i className="ri-file-text-fill text-blue-500 mr-1"></i>
                  <span className="font-medium text-gray-900">{policies}</span>
                </div>

                {/* Roadsides */}
                <div className="flex items-center">
                  <i className="ri-roadster-fill text-red-500 mr-1"></i>
                  <span className="font-medium text-gray-900">{roadsides}</span>
                </div>

                {/* BF Total */}
                <div className="flex items-center">
                  <i className="ri-money-dollar-box-fill text-green-500 mr-1"></i>
                  <span className="font-medium text-gray-900">${brokerFee.toLocaleString()}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${
                      rank === 1 ? 'bg-yellow-400' :
                      rank === 2 ? 'bg-gray-400' :
                      rank === 3 ? 'bg-orange-400' :
                      'bg-blue-400'
                    }`}
                    style={{
                      width: `${(policies / maxPolicies) * 100}%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PhilippinesSalesPage() {
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const adminEmails = ['admin@example.com', 'christine@example.com', 'jeremy@example.com'];
          setIsAdmin(adminEmails.includes(user.email || ''));
        } catch (err) {
          console.error('Error checking admin status:', err);
        }
      }
      setAdminCheckComplete(true);
    };

    checkAdminStatus();
  }, [user]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch data directly from Supabase table
      const { data: salesData, error: fetchError } = await supabase
        .from('philippines_sales_data')
        .select('*')
        .order('policies', { ascending: false });

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      setData(salesData || []);
    } catch (err) {
      console.error('Error fetching Philippines sales data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchData();
  };

  // Wait for both data and admin check to complete
  if (loading || !adminCheckComplete) {
    return (
      <div className="min-h-screen bg-orange-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-white font-medium">Loading Philippines sales data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-orange-400">
        <Header onRefresh={handleRefresh} isConnected={!error} showDashboardButton={isAdmin} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center">
              <i className="ri-error-warning-line text-red-600 text-xl mr-3"></i>
              <div>
                <h3 className="text-red-800 font-semibold">Error Loading Data</h3>
                <p className="text-red-600 mt-1">{error}</p>
                <button
                  onClick={fetchData}
                  className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate totals from all employees
  const totalPolicies = data.reduce((sum, item) => {
    const policies = parseInt(String(item.policies || '0').replace(/[^0-9]/g, '')) || 0;
    return sum + policies;
  }, 0);

  const totalBrokerFee = data.reduce((sum, item) => {
    const brokerFee = parseFloat(String(item.broker_fee || '0').replace(/[$,]/g, '')) || 0;
    return sum + brokerFee;
  }, 0);

  const totalRoadsides = data.reduce((sum, item) => {
    const roadsides = parseInt(String(item.roadsides || '0').replace(/[^0-9]/g, '')) || 0;
    return sum + roadsides;
  }, 0);

  const avgBrokerFee = data.length > 0 ? totalBrokerFee / data.length : 0;

  // Create stats object for updated StatsCard rendering
  const stats = {
    totalPolicies,
    totalBrokerFee,
    totalRoadsides,
    bfAverage: Math.round(avgBrokerFee),
  };

  // Calculate top performers from employee data
  const employees = data.filter(item => 
    item.underwriting_assistant && 
    item.underwriting_assistant.toLowerCase() !== 'total'
  );

  // Find top performers
  const topPolicies = employees.length > 0 ? employees.reduce((prev, current) => {
    const prevPolicies = parseInt(String(prev.policies || '0').replace(/[^0-9]/g, '')) || 0;
    const currentPolicies = parseInt(String(current.policies || '0').replace(/[^0-9]/g, '')) || 0;
    return currentPolicies > prevPolicies ? current : prev;
  }) : null;

  const topRoadsides = employees.length > 0 ? employees.reduce((prev, current) => {
    const prevRoadsides = parseInt(String(prev.roadsides || '0').replace(/[^0-9]/g, '')) || 0;
    const currentRoadsides = parseInt(String(current.roadsides || '0').replace(/[^0-9]/g, '')) || 0;
    return currentRoadsides > prevRoadsides ? current : prev;
  }) : null;

  const topBrokerFeeTotal = employees.length > 0 ? employees.reduce((prev, current) => {
    const prevBrokerFee = parseFloat(String(prev.broker_fee || '0').replace(/[$,]/g, '')) || 0;
    const currentBrokerFee = parseFloat(String(current.broker_fee || '0').replace(/[$,]/g, '')) || 0;
    return currentBrokerFee > prevBrokerFee ? current : prev;
  }) : null;

  // Transform top performers data for display
  const topPoliciesData = topPolicies ? {
    name: topPolicies.underwriting_assistant || 'Unknown',
    policies: parseInt(String(topPolicies.policies || '0').replace(/[^0-9]/g, '')) || 0,
    photo: getProfilePhoto(topPolicies.underwriting_assistant || 'Unknown')
  } : null;

  const topRoadsidesData = topRoadsides ? {
    name: topRoadsides.underwriting_assistant || 'Unknown',
    roadsides: parseInt(String(topRoadsides.roadsides || '0').replace(/[^0-9]/g, '')) || 0,
    photo: getProfilePhoto(topRoadsides.underwriting_assistant || 'Unknown')
  } : null;

  const topBrokerFeeTotalData = topBrokerFeeTotal ? {
    name: topBrokerFeeTotal.underwriting_assistant || 'Unknown',
    brokerFeeTotal: parseFloat(String(topBrokerFeeTotal.broker_fee || '0').replace(/[$,]/g, '')) || 0,
    photo: getProfilePhoto(topBrokerFeeTotal.underwriting_assistant || 'Unknown')
  } : null;

  return (
    <div className="min-h-screen bg-orange-400">
      <Header onRefresh={handleRefresh} isConnected={!error} showDashboardButton={isAdmin} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Philippines Sales MTD</h1>
          <p className="text-gray-600 mt-2">Month-to-date sales performance from Philippines team</p>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-center items-center mb-6 space-y-4 lg:space-y-0">
            <div className="flex-shrink-0 flex flex-wrap justify-center gap-3 max-w-6xl">
              <Link
                to="/philippines-sales-today"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-todo-line"></i>
                <span>PH Sales Today</span>
              </Link>

              {isAdmin && (
                <Link
                  to="/dashboard"
                  className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <i className="ri-dashboard-line"></i>
                  <span>Dashboard</span>
                </Link>
              )}
              
              <Link
                to="/mtd-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-check-line"></i>
                <span>MTD Sales</span>
              </Link>
              
              <Link
                to="/todays-sales"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-calendar-todo-line"></i>
                <span>Today's Sales</span>
              </Link>
              
              <Link
                to="/retention-mtd"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-user-heart-line"></i>
                <span>Retention MTD</span>
              </Link>

              <Link
                to="/retention-today"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-sky-500 text-white rounded-lg font-medium hover:bg-sky-600 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <i className="ri-user-heart-line"></i>
                <span>Retention Today</span>
              </Link>

              {isAdmin && (
                <Link
                  to="/suggestions-admin"
                  className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2"
                >
                  <i className="ri-lightbulb-line"></i>
                  <span>Suggestions</span>
                </Link>
              )}

              <Link
                to="/tv-view"
                className="flex-1 min-w-[140px] max-w-[180px] px-4 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium hover:from-purple-700 hover:to-indigo-700 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 flex items-center justify-center space-x-2 border-2 border-purple-300"
              >
                <i className="ri-tv-line"></i>
                <span>TV View</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatsCard
            title="Total Policies"
            value={stats.totalPolicies.toLocaleString()}
            icon="ri-file-list-3-line"
            color="blue"
          />
          <StatsCard
            title="Total Broker Fee"
            value={`$${stats.totalBrokerFee.toLocaleString()}`}
            icon="ri-money-dollar-circle-line"
            color="green"
          />
          <StatsCard
            title="BF Average"
            value={`$${stats.bfAverage.toLocaleString()}`}
            icon="ri-bar-chart-line"
            color="purple"
          />
          <StatsCard
            title="Total Roadsides"
            value={stats.totalRoadsides.toLocaleString()}
            icon="ri-roadster-line"
            color="orange"
          />
        </div>

        {/* Top Performers Section */}
        {topPoliciesData && topRoadsidesData && topBrokerFeeTotalData && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
              <i className="ri-trophy-line text-yellow-500 mr-3"></i>
              Top Performers
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Most Policies */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Most Policies</h3>
                  <i className="ri-trophy-fill text-3xl text-yellow-300"></i>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      {topPoliciesData.photo ? (
                        <img
                          src={topPoliciesData.photo}
                          alt={topPoliciesData.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<i class="ri-user-line text-white text-xl"></i>`;
                            }
                          }}
                        />
                      ) : (
                        <i className="ri-user-line text-white text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{topPoliciesData.name}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-3xl font-black">{topPoliciesData.policies.toLocaleString()}</p>
                    <p className="text-sm text-blue-100">Policies Sold</p>
                  </div>
                </div>
              </div>

              {/* Most Roadsides */}
              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Most Roadsides</h3>
                  <i className="ri-car-line text-3xl text-green-200"></i>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      {topRoadsidesData.photo ? (
                        <img
                          src={topRoadsidesData.photo}
                          alt={topRoadsidesData.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<i class="ri-user-line text-white text-xl"></i>`;
                            }
                          }}
                        />
                      ) : (
                        <i className="ri-user-line text-white text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{topRoadsidesData.name}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-3xl font-black">{topRoadsidesData.roadsides.toLocaleString()}</p>
                    <p className="text-sm text-green-100">Roadsides Sold</p>
                  </div>
                </div>
              </div>

              {/* Highest BF Total */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Highest BF Total</h3>
                  <i className="ri-money-dollar-box-fill text-3xl text-yellow-300"></i>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-white bg-opacity-20 flex items-center justify-center flex-shrink-0">
                      {topBrokerFeeTotalData.photo ? (
                        <img
                          src={topBrokerFeeTotalData.photo}
                          alt={topBrokerFeeTotalData.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent) {
                              parent.innerHTML = `<i class="ri-user-line text-white text-xl"></i>`;
                            }
                          }}
                        />
                      ) : (
                        <i className="ri-user-line text-white text-xl"></i>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-lg truncate">{topBrokerFeeTotalData.name}</p>
                    </div>
                  </div>
                  <div className="bg-white bg-opacity-20 rounded-lg p-3">
                    <p className="text-3xl font-black">${Math.round(topBrokerFeeTotalData.brokerFeeTotal).toLocaleString()}</p>
                    <p className="text-sm text-purple-100">Total Broker Fee</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Individual Underwriting Assistant Performance */}
        <div className="mb-8">
          <IndividualPerformance data={data} />
        </div>

        {/* Performance Rankings */}
        <div className="mb-8">
          <PerformanceRankings data={data} />
        </div>
      </div>
    </div>
  );
}
